import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AutoComplete, Layout, Menu, ConfigProvider, Input, Avatar, Typography, Button, Spin, message } from 'antd';
import { 
  DashboardOutlined, 
  TransactionOutlined, 
  CreditCardOutlined, 
  FileTextOutlined, 
  LineChartOutlined, 
  AimOutlined, 
  PlusOutlined, 
  SyncOutlined, 
  SettingOutlined, 
  QuestionCircleOutlined, 
  SearchOutlined, 
  BellOutlined, 
  MailOutlined,
  LogoutOutlined,
  EditOutlined,
  HomeOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import EstateList from '../estatelist/estatelist';
import EstateDetails from '../estatelist/estatedetails';
import DocumentsPage from '../documents/probatedocs';
import CalendarComponent from '../calendar/calendar';
import AssetPage from '../assetform/assetform';
import api from '../../services/api';
import AssetsListPage from '../assets/assetlist';
import LiabilitiesListPage from '../liabilities/liabilities';
import CashFlowListPage from '../cashflows/cashflows';
import FamilyJewelsDashboardPage from '../familyjewels/familyjewels';
import { useGlobalContext } from '../../store/GlobalContext';
import RequireEstate from './RequireEstate';
import ParticipantsPage from '../participant/participant';
import TreeRenderer from '../familytree/tree';
import AccountPage from '../account/AccountPage';
import HelpPage from '../help/help';
import NotificationBell from '../notification/notificationBell';
import ResetPasswordPage from '../resetpassword/resetpassword';
import Cookies from 'js-cookie';
import EstateSelectorModal from '../estates/estateselector';
import FieldMappingPage from '../documents/FieldMappingPage';
import PdfVisualMapper from '../documents/PdfVisualMapper';
import AssetDetails from '../assets/assetdetails';
import LiabilityDetails from '../liabilities/liabilitydetails';
import CashflowDetails from '../cashflows/cashflowdetails';
import ParticipantDetails from '../participant/participantdetails';
import ProbateDocDetails from '../documents/probatedocdetails';
import debounce from 'lodash.debounce';
import {
  globalSearch,
  searchResultPath,
  SearchResult,
  SEARCH_GROUP_LABELS,
  SEARCH_GROUP_ORDER,
} from '../../services/search.service';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const getInitials = (firstName?: string | null, lastName?: string | null, email?: string | null) => {
  if (firstName && lastName) return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  if (firstName) return firstName.slice(0, 2).toUpperCase();
  if (lastName) return lastName.slice(0, 2).toUpperCase();
  if (email) return email.slice(0, 2).toUpperCase();
  return 'U';
};

// Sidebar Header Logo and Search Container
const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  gap: 0.5rem;
  border-bottom: 1px solid #e0e0e0;
`;

// Logo component
const Logo = styled.div<{ collapsed: boolean }>`
  font-size: 1.5rem;
  font-weight: bold;
  color: #3b82f6;
  display: ${props => (props.collapsed ? 'none' : 'block')};
`;

// Styled Avatar Container for User Info at Bottom of Sidebar
const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  border-top: 1px solid #e0e0e0;
  padding-bottom: 50px;
  bottom: 0;

`;

// Avatar styling
const CollapsibleAvatar = styled(Avatar)`
  width: 40px;
  height: 40px;
`;

// Sidebar Menu Item Icon Style
const IconWrapper = styled.span<{ collapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => (props.collapsed ? '1.5rem' : '1rem')};
  margin-right: ${props => (props.collapsed ? '0' : '1rem')};
`;

// Top Navigation Search and Icons Container
const TopNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 1rem 2rem;
  background: #f4f6fb;
`;

const SearchInput = styled(Input)`
  width: 300px;
  .ant-input-prefix {
    color: #888;
  }
`;

const SearchGroupLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: #888888;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const SearchResultTitle = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SearchResultMeta = styled.div`
  font-size: 0.75rem;
  color: #888888;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/*
 * Every estate-scoped page reads from one active estate, and which one that is
 * was only visible inside the selector modal. Naming it in the top bar, beside
 * the control that changes it, keeps the answer on screen at all times.
 */
const ActiveEstate = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  /* Matches the buttons it sits beside: same height, radius and hairline, so
     the row lines up instead of the label floating as its own pill. */
  height: 32px;
  padding: 0 0.75rem;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: #ffffff;
  max-width: 320px;
`;

const ActiveEstateName = styled(Text)`
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IconButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const siderStyle: React.CSSProperties = {
  overflow: 'auto',
  height: '100vh',
  position: 'fixed',
  insetInlineStart: 0,
  top: 0,
  bottom: 0,
  paddingBottom: '50px',
  scrollbarWidth: 'thin',
  scrollbarGutter: 'stable',
  background: '#ffffff',
};

function Dashboard() {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation(); // Get the current route
  const { auth, selectedEstate, setAuth } = useGlobalContext();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchScope, setSearchScope] = useState<'active' | 'all'>('active');
  const [searching, setSearching] = useState<boolean>(false);
  const [searchFocused, setSearchFocused] = useState<boolean>(false);
  const { user } = auth;

  /*
   * Responses can land out of order, so each request carries a sequence number
   * and only the newest is allowed to write to state. Without it a slow reply
   * for "sm" arrives after "smith" and repopulates the list with results for a
   * query the user has already typed past.
   */
  const searchSeq = useRef(0);

  /*
   * The active estate is read through a ref so that changing estates does not
   * rebuild the debounced function and drop a search already in flight.
   */
  const activeEstateId = useRef<string | undefined>(selectedEstate?.uuid);
  activeEstateId.current = selectedEstate?.uuid;

  const runSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        const seq = searchSeq.current;
        try {
          const response = await globalSearch(query, activeEstateId.current);
          if (seq === searchSeq.current) {
            setSearchResults(response.results);
            setSearchScope(response.scope);
          }
        } catch (error) {
          if (seq === searchSeq.current) setSearchResults([]);
        } finally {
          if (seq === searchSeq.current) setSearching(false);
        }
      }, 300),
    [],
  );

  useEffect(() => () => runSearch.cancel(), [runSearch]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    searchSeq.current += 1;

    if (!value.trim()) {
      runSearch.cancel();
      setSearchResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    runSearch(value.trim());
  };

  const handleSearchSelect = (value: string) => {
    const result = searchResults.find((item) => `${item.type}:${item.id}` === value);
    runSearch.cancel();
    searchSeq.current += 1;
    setSearchTerm('');
    setSearchResults([]);
    setSearching(false);

    if (result) {
      navigate(searchResultPath(result));
    }
  };

  /*
   * Grouped by entity type so a name that matches an asset and a participant
   * does not read as one undifferentiated list. Options carry a "type:id" value
   * because ids are only unique within a type.
   */
  const searchOptions = SEARCH_GROUP_ORDER.map((type) => {
    const group = searchResults.filter((result) => result.type === type);
    if (!group.length) return null;

    return {
      label: <SearchGroupLabel>{SEARCH_GROUP_LABELS[type]}</SearchGroupLabel>,
      options: group.map((result) => ({
        value: `${result.type}:${result.id}`,
        label: (
          <div>
            <SearchResultTitle>{result.title}</SearchResultTitle>
            {/* Which file a result came from only matters once search spans
                more than the open estate. */}
            {searchScope === 'all' && result.estateName && (
              <SearchResultMeta>{result.estateName}</SearchResultMeta>
            )}
          </div>
        ),
      })),
    };
  }).filter(Boolean) as { label: React.ReactNode; options: { value: string; label: React.ReactNode }[] }[];

  // Logout Handler
  const handleLogout = async () => {
    // End the session on the server first. Clearing cookies only makes this
    // browser forget its tokens; the refresh token stayed good for another
    // thirty days, so on a shared machine signing out did not sign you out.
    //
    // A failure here is not worth blocking on: the person asked to be logged
    // out and the local half of that always works.
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Failed to end the session on the server:', error);
    }

    setAuth({
      ...auth,
      user: null,
      accessToken: null,
    });
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedEstate');

    //clear cookies
    Cookies.remove('user');
    Cookies.remove('accessToken');
    Cookies.remove('selectedEstate');
    
    message.success('Logged out successfully');
    navigate('/login');
  };

  // Define configuration arrays for different sections of the menu
  const mainMenuItems = [
    { key: '/estates', icon: <DashboardOutlined />, label: 'Estates' },
    { key: '/family-jewels', icon: <TransactionOutlined />, label: 'Family Jewels' },
    { key: '/probate-docs', icon: <CreditCardOutlined />, label: 'Probate Docs' },
    { key: '/calendar', icon: <FileTextOutlined />, label: 'Calendar' },
    { key: '/family-tree', icon: <AimOutlined />, label: 'Family Tree' },
    
  ];

  const quickLinksItems = [
    { key: '/assets', icon: <PlusOutlined />, label: 'Assets' },
    { key: '/liabilities', icon: <CreditCardOutlined />, label: 'Liabilities' },
    { key: '/cashflows', icon: <LineChartOutlined />, label: 'Cashflows' },
    { key: '/participants', icon: <AimOutlined />, label: 'Participants' },
  ];

  const bottomMenuItems = [
    { key: '/account', icon: <SettingOutlined />, label: 'Account' },
    { key: '/help-center', icon: <QuestionCircleOutlined />, label: 'Help Center' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', action: handleLogout },
  ];

  // Get the currently selected menu key based on the route
  const currentKey = location.pathname;

  return (
    <ConfigProvider>
      <Layout style={{ minHeight: '100vh' }}>
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          width={250}
          style={siderStyle}
        >
          {/* Sidebar Header */}
          <SidebarHeader>
            <Logo collapsed={collapsed}>Wzee</Logo>
            <Button
              type="text"
              icon={<SyncOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ marginLeft: 'auto', color: '#3b82f6' }}
            />
          </SidebarHeader>

          {/* Main Menu */}
          <Menu mode="inline" selectedKeys={[currentKey]} style={{ border: 'none' }}>
            {mainMenuItems.map(({ key, icon, label }) => (
              <Menu.Item
                key={key}
                icon={<IconWrapper collapsed={collapsed}>{icon}</IconWrapper>}
                onClick={() => navigate(key)}
              >
                {!collapsed && label}
              </Menu.Item>
            ))}
            <Menu.Item
                key="change-active-estate"
                icon={<IconWrapper collapsed={collapsed}>{<EditOutlined />}</IconWrapper>}
                onClick={() => setModalVisible(true)}
              >
                {!collapsed && "Change Active Estate"}
            </Menu.Item>
            
          </Menu>

          {/* Quick Links Section */}
          <Menu mode="inline" selectedKeys={[currentKey]} style={{ borderTop: '1px solid #e0e0e0', marginTop: '1rem' }}>
            {quickLinksItems.map(({ key, icon, label }) => (
              <Menu.Item
                key={key}
                icon={<IconWrapper collapsed={collapsed}>{icon}</IconWrapper>}
                onClick={() => navigate(key)}
              >
                {!collapsed && label}
              </Menu.Item>
            ))}
          </Menu>

          {/* Bottom Section */}
          <Menu mode="inline" selectedKeys={[currentKey]} style={{ borderTop: '1px solid #e0e0e0', marginTop: '1rem' }}>
            {bottomMenuItems.map(({ key, icon, label, action }) => (
              <Menu.Item
                key={key}
                icon={<IconWrapper collapsed={collapsed}>{icon}</IconWrapper>}
                onClick={() => {
                  if (action) {
                    action();
                  } else {
                    navigate(key);
                  }
                }}
              >
                {!collapsed && label}
              </Menu.Item>
            ))}
          </Menu>

          {/* User Info */}
          <UserInfo>
            <CollapsibleAvatar src={user?.profilePic}>
              {!user?.profilePic && getInitials(user?.firstName, user?.lastName, user?.email)}
            </CollapsibleAvatar>
            {!collapsed && (
              <div>
                <Text strong>{user?.firstName || 'User'}</Text> <br />
                <Text type="secondary">{user?.email || 'user@example.com'}</Text>
              </div>
            )}
          </UserInfo>
        </Sider>

        <Layout style={{ marginLeft: collapsed ? 80 : 250 }}>
          <Header
            style={{
              background: '#f4f6fb',
              padding: 0,
              position: 'fixed',
              width: `calc(100% - ${collapsed ? 80 : 250}px)`,
              zIndex: 1,
            }}
          >
            <TopNav>
              <div
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
              >
                <AutoComplete
                  /* The dropdown sizes itself from this element, not from the
                     input inside it, so it needs the width too. */
                  style={{ width: 300 }}
                  value={searchTerm}
                  options={searchOptions}
                  onChange={handleSearchChange}
                  onSelect={handleSearchSelect}
                  /* Left uncontrolled, the dropdown opens on focus and shows an
                     empty panel before anything has been typed. */
                  open={searchFocused && searchTerm.trim().length > 0}
                  notFoundContent={searching ? <Spin size="small" /> : 'No results found'}
                >
                  <SearchInput placeholder="Search..." prefix={<SearchOutlined />} allowClear />
                </AutoComplete>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {/* <BellOutlined /> */}
                <ActiveEstate>
                  <HomeOutlined style={{ color: '#3b82f6' }} />
                  {selectedEstate ? (
                    <ActiveEstateName ellipsis title={selectedEstate.estateName}>
                      {selectedEstate.estateName}
                    </ActiveEstateName>
                  ) : (
                    <Text type="secondary">No estate selected</Text>
                  )}
                </ActiveEstate>
                <Button type="dashed" onClick={() => setModalVisible(true)} >Change Active Estate</Button>
                <NotificationBell /> 
                {/* account icon button */}
                <Button type="text" icon={
                  <Avatar src={user?.profilePic} onClick={() => navigate('/account')}>
                    {!user?.profilePic && getInitials(user?.firstName, user?.lastName, user?.email)}
                  </Avatar>
                } />
              </div>
            </TopNav>
          </Header>

          <Content style={{ padding: '2rem', marginTop: 64, background: '#f4f6fb' }}>
          <EstateSelectorModal visible={modalVisible} onClose={() => setModalVisible(false)} />
            <Routes>
              {/* Wrap estate-specific routes with RequireEstate */}
              <Route
                path="/assets/*"
                element={
                  <RequireEstate>
                    <AssetsListPage />
                  </RequireEstate>
                }
              />
              <Route
                path="/cashflows/*"
                element={
                  <RequireEstate>
                    <CashFlowListPage />
                  </RequireEstate>
                }
              />
              <Route
                path="/liabilities/*"
                element={
                  <RequireEstate>
                    <LiabilitiesListPage />
                  </RequireEstate>
                }
              />
              <Route
                path="/probate-docs"
                element={
                  <RequireEstate>
                    <DocumentsPage />
                  </RequireEstate>
                }
              />
              <Route
                path="/probate-docs/:probateDocId/map-fields"
                element={
                  <RequireEstate>
                    <FieldMappingPage />
                  </RequireEstate>
                }
              />
              <Route
                path="/probate-docs/:probateDocId/visual-mapper"
                element={
                  <RequireEstate>
                    <PdfVisualMapper />
                  </RequireEstate>
                }
              />
              <Route
                path="/family-jewels/*"
                element={
                  <RequireEstate>
                    <FamilyJewelsDashboardPage />
                  </RequireEstate>
                }
              />
              {/* Entity detail pages. Declared after the `/*` list routes above
                  but more specific than them, so the router picks these for a
                  single id and the list page for everything else. */}
              <Route
                path="/assets/:assetId"
                element={
                  <RequireEstate>
                    <AssetDetails />
                  </RequireEstate>
                }
              />
              <Route
                path="/liabilities/:liabilityId"
                element={
                  <RequireEstate>
                    <LiabilityDetails />
                  </RequireEstate>
                }
              />
              <Route
                path="/cashflows/:cashflowId"
                element={
                  <RequireEstate>
                    <CashflowDetails />
                  </RequireEstate>
                }
              />
              <Route
                path="/participants/:participantId"
                element={
                  <RequireEstate>
                    <ParticipantDetails />
                  </RequireEstate>
                }
              />
              <Route
                path="/probate-docs/:probateDocId"
                element={
                  <RequireEstate>
                    <ProbateDocDetails />
                  </RequireEstate>
                }
              />
              <Route path="/dashboard" element={<EstateList />} />
              <Route path="/estates" element={<EstateList />} />
              <Route path="/estate/:estateId" element={<EstateDetails />} />
              <Route path="/calendar" element={<CalendarComponent isAdmin />} />
              <Route path="/asset-form" element={<AssetPage />} />
              <Route path="/participants" element={<ParticipantsPage />} />
              <Route path="/family-tree" element={<TreeRenderer />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/help-center" element={<HelpPage />} />
              

            </Routes>
          </Content>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
}

export default Dashboard;
